import { useDepot } from '#~/depot/hooks.js';
import type { Discovery } from '#~/device/discovery/index.js';
// Transitory-placeholder liveness (Multi-Add Phase 2, spec §7 / §17.2#7):
// proactive ONVIF eviction is diff/sweep-based (there is NO mdns:deviceLost
// event @6370b897b), so the slice observes the room's discovery set each
// tick and evicts a placeholder whose bound discovery stays absent ~15s.
// Per `reference-rules-in-comments` (ai.knowledge/global).
import {
  observeTransitoryLiveness,
  registerTransitoryLivenessAnalytics,
  registerTransitoryLivenessToast,
  stopTransitoryLivenessSweep
} from '#~/device/transitory.js';
import * as Notifications from '#~/notifications/index.js';
import { EVENT_NAMES } from '#~/analytics/constants/event-names.js';
import { emitMultiAddAnalytics } from '#~/analytics/utils/emit-multi-add-analytics.js';
import { Context } from './context.js';
import { dequal } from 'dequal/lite';
import { useContext, useEffect } from 'react';

/**
 * Room-scoped watcher that drives proactive ONVIF placeholder liveness
 * (spec §7 / §17.2#7). Mounted INSIDE the room `Context` so it can read the
 * room's reactive discovery set. It feeds that set to the transitory slice
 * each time the set changes; the slice's single sweep timer evicts a
 * placeholder whose bound discovery stays absent past the ~15s debounce.
 *
 * Renders nothing — it is a side-effect host, kept in its OWN module (not
 * inlined in `Provider`) for two reasons: `Provider` sits ABOVE the `Context`
 * it supplies and so cannot call `useRoom`; and isolating the watcher keeps
 * its dependency graph small enough to render under jest, which is what makes
 * the defensive-read regression test possible (see
 * `transitory-liveness-watcher.test.tsx`).
 *
 * Per `think-in-systems` (ai.knowledge/global): the eviction logic lives in
 * the slice (single source of truth); this component is the thin reactive
 * adapter. Per `reference-rules-in-comments` (ai.knowledge/global).
 */
/**
 * Narrow a discovery aspect config (a union — not every transport carries a
 * network host, e.g. VISCA_SERIAL / Local) down to its `host` string when it
 * has one. Runtime-safe across all variants.
 */
const aspectHost = (config: unknown): string | undefined => {
  if (config && typeof config === 'object' && 'host' in config) {
    const { host } = config as { host?: unknown };
    return typeof host === 'string' ? host : undefined;
  }
  return undefined;
};

/**
 * Normalize a derived discovery host to the bare-host form placeholders
 * carry: Iris-enabled discoveries mint a COMPOSITE `"host:port"` address
 * (`add/source/selector.tsx`) which `buildPlaceholderForDiscovery` strips to
 * the bare host (`transitory-from-discovery.ts::resolveHostPort`). Mirror
 * that exact first-colon strip here so `presentHosts` and
 * `placeholder.address` always compare in the same form — otherwise
 * host-keyed survival silently skips composite-address cameras.
 */
const normalizeHost = (host: string): string =>
  host.includes(':') ? host.slice(0, host.indexOf(':')) : host;

export const TransitoryLivenessWatcher = (): null => {
  // Reactive, order-stable sets of discovery ids AND hosts currently in the
  // room. Sorted so `dequal` only re-fires when a SET actually changes, not
  // when the agent reorders its discovery map between ticks.
  //
  // Read the room slice DEFENSIVELY (NOT via `useRoom`): this watcher renders
  // eagerly inside `Context.Provider` — a sibling of the routed `children`,
  // which ARE gated by `/boot`. But `Provider` writes `rooms[roomId]` only
  // later, inside its async effect. On that first cold-boot render the slice
  // is absent, so `useRoom`→`Slicer.room` would `ensureNotNil`-throw
  // "Unexpected `T_Nil`" and crash the whole room subtree (repro'd from
  // renderer logs: slicer.ts ← room/hooks.ts useRoom). Reading via `useDepot`
  // keyed on the context roomId tolerates the gap: absent room ⇒ empty set ⇒
  // the sweep idles until state-sync loads the room.
  // Per `stay-in-scope` + `reference-rules-in-comments` (ai.knowledge/global).
  const roomId = useContext(Context);

  const discoverySignal = useDepot((µ) => {
    const discoveries = (roomId ? µ.rooms[roomId] : undefined)?.discoveries ?? {};
    return {
      // HOST-KEYED SURVIVAL (see `observeTransitoryLiveness`): the distinct
      // addresses currently announced, so a placeholder survives while ANY
      // discovery at its address is present (dual-transport twins flap
      // independently and re-announcements can mint new ids).
      //
      // `address` is optional and FREQUENTLY UNSET on ONVIF-sourced
      // entries — their host travels in the control (or video) aspect
      // config instead. Reading only `address` left ONVIF-only cameras
      // unprotected (HIL-verified: an ONVIF-only placeholder armed and
      // evicted at absentMs=15002 while its camera sat visibly in
      // discovery, because presentHosts never contained its IP). Use the
      // same fallback chain the rest of the codebase uses.
      hosts: [
        ...new Set(
          Object.values(discoveries)
            .map(
              (discovery) =>
                discovery.address ?? aspectHost(discovery.control) ?? aspectHost(discovery.video)
            )
            .filter((address): address is string => typeof address === 'string' && address !== '')
            .map(normalizeHost)
        )
      ].sort(),
      ids: Object.keys(discoveries).sort()
    };
  }, dequal);

  // Register the toast emitter once (the slice stays framework-agnostic and
  // never imports the toast primitive directly — testable under jest).
  useEffect(() => {
    registerTransitoryLivenessToast((placeholder) =>
      Notifications.post({
        // Posted only by the sweep, after it removed a placeholder whose
        // camera stayed absent on every transport. Auth failures never land here.
        message: `${placeholder.name} is no longer available — re-add it.`,
        severity: 'warning',
        title: 'Device unavailable'
      })
    );

    return () => {
      registerTransitoryLivenessToast(undefined);
    };
  }, []);

  // Register the analytics emitter (parallel to the toast). Fires only when
  // the sweep evicts a placeholder whose camera vanished — `liveness-removed`
  // distinguishes it from auth-success / user-remove in the funnel.
  // Per `reference-rules-in-comments` (ai.knowledge/global).
  useEffect(() => {
    registerTransitoryLivenessAnalytics((placeholder) =>
      emitMultiAddAnalytics(EVENT_NAMES.MULTI_ADD_DEVICE_NEEDS_ATTENTION, {
        deviceId: placeholder.id.toString(),
        deviceName: placeholder.name,
        skipReason: 'liveness-removed'
      })
    );

    return () => {
      registerTransitoryLivenessAnalytics(undefined);
    };
  }, []);

  // Feed the diff/sweep signal whenever the discovery id/host sets change.
  useEffect(() => {
    observeTransitoryLiveness(
      new Set(discoverySignal.ids as Discovery['id'][]),
      new Set(discoverySignal.hosts)
    );
  }, [discoverySignal]);

  // Release the single sweep timer on room teardown.
  useEffect(
    () => () => {
      stopTransitoryLivenessSweep();
    },
    []
  );

  return null;
};
